import {
    ClearIndicatorProps,
    components,
    DropdownIndicatorProps,
    GroupBase,
    InputProps,
    MenuListProps,
    MultiValueProps,
    MultiValueRemoveProps,
    OptionProps,
} from 'react-select';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

import MHToolTip from '@/components/base/MHToolTip';
import {MHTypography} from '@/components/base/MHTypography';

import MHLoader from '../MHLoader';
import {TypedOption} from './types';

import styles from './styles/componentOverrides.module.scss';

const toMhSelectOptionTestId = (label: string): string =>
    `mh-select-option-${String(label)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '')}`;

const DropdownIndicator = <Option, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: DropdownIndicatorProps<Option, IsMulti, Group>,
) => (
    <components.DropdownIndicator {...props}>
        <ArrowDropDownIcon />
    </components.DropdownIndicator>
);

const ClearIndicator = <Option, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: ClearIndicatorProps<Option, IsMulti, Group>,
) => (
    <components.ClearIndicator {...props}>
        <div className={styles.removeAllText}>Clear</div>
    </components.ClearIndicator>
);

const LoadingIndicator = () => <MHLoader className={styles.loadingIcon} />;

const MultiValueRemove = <Option, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: MultiValueRemoveProps<Option, IsMulti, Group>,
) => (
    <components.MultiValueRemove
        {...props}
        innerProps={
            {
                ...props.innerProps,
                'data-testid': `remove-${String((props.data as {label?: string}).label ?? '').toLowerCase()}-chip`,
            } as unknown
        }
    >
        <CloseIcon />
    </components.MultiValueRemove>
);

const Option = <Option, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: OptionProps<Option, IsMulti, Group>,
) => {
    // Extract onClick from innerProps and place it on the outer wrapper alongside data-testid.
    // This guarantees the attribute is in the DOM (Emotion's JSX transform can drop it from
    // components.Option's innerProps in practice). Removing onClick from innerProps prevents
    // the handler from firing twice when a child element (e.g. checkbox) is clicked.
    const {onClick, ...innerPropsWithoutClick} = props.innerProps as unknown as {
        onClick?: (event: MouseEvent) => void;
        [key: string]: unknown;
    };

    return (
        <div
            data-testid={toMhSelectOptionTestId(String(props.label))}
            onClick={onClick as unknown as React.MouseEventHandler<HTMLDivElement>}
        >
            <components.Option {...props} innerProps={innerPropsWithoutClick}>
                <FormControlLabel
                    classes={{root: styles.multiOptionLabel}}
                    control={
                        <Checkbox
                            classes={{root: styles.multiOptionCheckbox}}
                            checked={props.isSelected}
                            color="primary"
                        />
                    }
                    label={props.label}
                />
            </components.Option>
        </div>
    );
};

const MultiValue = (props: MultiValueProps<TypedOption<string, string>> & {multiValueMaxVisible?: number}) => {
    const {index, getValue, multiValueMaxVisible} = props;
    const value = getValue();
    const visibleValue = value.slice(multiValueMaxVisible).map((el) => el.label);
    const tooltipContent = visibleValue.map((el) => <div key={el}>{el}</div>);

    return !multiValueMaxVisible || index < multiValueMaxVisible ? (
        <components.MultiValue {...props}>{props.children}</components.MultiValue>
    ) : index === multiValueMaxVisible ? (
        <MHToolTip tooltipContent={tooltipContent}>
            <MHTypography
                marginLeft="4px"
                variant="labelM"
                color="teal_100"
            >{`+${tooltipContent.length}`}</MHTypography>
        </MHToolTip>
    ) : null;
};

const MenuList = <Option, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>>({
    menuTestId,
    ...props
}: MenuListProps<Option, IsMulti, Group> & {menuTestId?: string}) => (
    <div data-testid={menuTestId ?? 'mh-select-menu-list'}>
        <components.MenuList {...props} />
    </div>
);

const SingleOption = <Option, IsMulti extends boolean, Group extends GroupBase<Option> = GroupBase<Option>>(
    props: OptionProps<Option, IsMulti, Group>,
) => (
    <components.Option
        {...props}
        innerProps={
            {
                ...props.innerProps,
                'data-testid': toMhSelectOptionTestId(String(props.label)),
            } as unknown
        }
    />
);

const createSearchInput = (inputTestId: string) => (props: InputProps<unknown, boolean, GroupBase<unknown>>) => (
    <components.Input {...props} data-testid={inputTestId} />
);

export {
    ClearIndicator,
    createSearchInput,
    DropdownIndicator,
    LoadingIndicator,
    MenuList,
    MultiValue,
    MultiValueRemove,
    Option,
    SingleOption,
};
